import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Accessibility, Bell, Menu, ChevronDown, LogOut, LayoutDashboard, Settings, Building2, Briefcase, Shield, Sparkles } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "../ui/dropdown-menu";
import { useAuth } from "../../context/AuthContext";
import { useA11y } from "../../context/A11yContext";
import { useFetch } from "../../lib/api";
import { btn } from "../kit";

export const NAV = [
  { to: "/jobs", label: "Jobs" },
  { to: "/freelancing", label: "Freelancing" },
  { to: "/government", label: "Government Opportunities" },
  { to: "/community", label: "Community" },
];

function Wordmark() {
  return (
    <Link
      to="/"
      className="flex min-h-[44px] items-center gap-1.5 sm:gap-2.5"
      data-testid="header-logo-link"
      aria-label="OpenPath home"
    >
      <img
        src="/openpath-logo.png"
        alt=""
        className="h-9 w-9 shrink-0 object-contain sm:h-10 sm:w-10"
        aria-hidden="true"
      />
      <span className="hidden text-lg font-extrabold tracking-tight text-navy min-[360px]:inline sm:text-2xl">
        OpenPath
      </span>
    </Link>
  );
}

function UserMenu() {
  const { user, logout, has } = useAuth();
  const nav = useNavigate();
  const items = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, show: true },
    { to: "/career-ai", label: "AI Career Assistant", icon: Sparkles, show: true },
    { to: "/employer", label: "Employer hub", icon: Building2, show: has("employer") },
    { to: "/client", label: "Client projects", icon: Briefcase, show: has("client") },
    { to: "/admin", label: "Admin", icon: Shield, show: has("admin") },
    { to: "/settings", label: "Settings & privacy", icon: Settings, show: true },
  ];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className={btn("secondary", "sm")} data-testid="user-menu-trigger">
        <span className="grid h-7 w-7 place-items-center rounded-full bg-mist text-navy text-sm font-bold" aria-hidden="true">{user.name?.[0]?.toUpperCase()}</span>
        <span className="hidden lg:inline max-w-[10rem] truncate">{user.name}</span><span className="sr-only lg:hidden">Account menu</span>
        <ChevronDown className="h-4 w-4" aria-hidden="true" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="truncate">{user.email}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.filter((i) => i.show).map((i) => (
          <DropdownMenuItem key={i.to} onSelect={() => nav(i.to)} className="min-h-[44px] text-base cursor-pointer" data-testid={`user-menu-${i.to.slice(1)}`}>
            <i.icon className="mr-2 h-4 w-4" aria-hidden="true" />{i.label}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={async () => { await logout(); nav("/"); }} className="min-h-[44px] text-base cursor-pointer" data-testid="sign-out-btn">
          <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function NotifBell() {
  const { data } = useFetch("/me/notifications?unread=true");
  const n = data?.unread || 0;
  return (
    <Link to="/notifications" className={`${btn("secondary", "sm")} relative !px-3`} data-testid="notifications-link" aria-label={`Notifications${n ? `, ${n} unread` : ""}`}>
      <Bell className="h-5 w-5" aria-hidden="true" />
      {n > 0 && <span className="absolute -right-1.5 -top-1.5 min-w-[22px] rounded-full bg-err px-1.5 text-center text-xs font-bold leading-[22px] text-white" aria-hidden="true" data-testid="notifications-unread-count">{n}</span>}
    </Link>
  );
}

const navCls = ({ isActive }) => `relative flex min-h-[44px] items-center px-3 font-semibold rounded-md hover:bg-mist ${isActive ? "text-blue-hover after:absolute after:inset-x-3 after:-bottom-[13px] after:h-[3px] after:rounded after:bg-blue" : "text-navy"}`;

export default function Header() {
  const { user } = useAuth();
  const { setOpen } = useA11y();
  const [mobile, setMobile] = useState(false);
  return (
    <header className="sticky top-0 z-40 border-b border-line bg-white" data-testid="site-header">
      <div className="container-x flex h-[72px] items-center justify-between gap-2 sm:gap-4">
        <Wordmark />
        <nav aria-label="Main" className="hidden xl:block">
          <ul className="flex items-center gap-1">
            {NAV.map((n) => <li key={n.to}><NavLink to={n.to} className={navCls} data-testid={`nav-${n.to.slice(1)}`}>{n.label}</NavLink></li>)}
          </ul>
        </nav>
        <div className="flex items-center gap-1 sm:gap-2">
          <button onClick={() => setOpen(true)} className={btn("secondary", "sm")} data-testid="accessibility-panel-toggle" aria-label="Accessibility settings">
            <Accessibility className="h-5 w-5" aria-hidden="true" /><span className="hidden md:inline">Accessibility</span>
          </button>
          {user && <NotifBell />}
          {user ? <UserMenu /> : user === false && (
            <div className="hidden sm:flex items-center gap-2">
              <Link to="/signin" className={btn("ghost", "sm")} data-testid="header-signin-link">Sign in</Link>
              <Link to="/signup" className={btn("primary", "sm")} data-testid="header-signup-link">Create account</Link>
            </div>
          )}
          <button className={`${btn("secondary", "sm")} xl:hidden`} onClick={() => setMobile(true)} aria-expanded={mobile} aria-controls="mobile-nav" aria-label="Open menu" data-testid="mobile-menu-toggle">
            <Menu className="h-5 w-5" aria-hidden="true" /><span className="hidden sm:inline">Menu</span>
          </button>
        </div>
      </div>
      <Sheet open={mobile} onOpenChange={setMobile}>
        <SheetContent side="right" id="mobile-nav" className="w-[88vw] max-w-sm bg-white" data-testid="mobile-nav">
          <SheetHeader>
            <SheetTitle className="text-navy">Menu</SheetTitle>
            <SheetDescription>Browse OpenPath sections</SheetDescription>
          </SheetHeader>
          <nav aria-label="Mobile" className="mt-6">
            <ul className="space-y-1">
              {[...NAV, ...(user ? [{ to: "/dashboard", label: "Dashboard" }, { to: "/career-ai", label: "AI Career Assistant" }, { to: "/settings", label: "Settings" }] : [{ to: "/signin", label: "Sign in" }, { to: "/signup", label: "Create account" }])].map((n) => (
                <li key={n.to}><NavLink to={n.to} onClick={() => setMobile(false)} className={({ isActive }) => `flex min-h-[48px] items-center rounded-lg px-3 text-lg font-semibold ${isActive ? "bg-mist text-blue-hover" : "text-navy"}`} data-testid={`mobile-nav-${n.to.slice(1)}`}>{n.label}</NavLink></li>
              ))}
            </ul>
          </nav>
        </SheetContent>
      </Sheet>
    </header>
  );
}
